// Componente UI reutilizabile, prezentaționale (fără stare din MainApp):
// iconițe, badge-uri, dropdown-uri, selectoare de dată/oră, modale, comutatoare.
import React, { useState, useEffect } from "react";
import { fmConfirm } from './dialog';
import { isValidPhone, getDIM, getFDOW, foldDia } from './helpers';
import { MONTHS, WDAYS, CAREGIVER_PALETTE, JUDETE, loadLocalities } from './data';
import { t } from './i18n';

export function IconTrash({size=16, color="currentColor"}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:"inline-block",verticalAlign:"middle"}}>
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6M14 11v6"/>
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  );
}

// Indicator de încărcare cu inima aplicației, plină, care bate și se învârte (clasa .heartspin).
// Aceeași inimă ca în restul aplicației (glif plin, mov) — folosită peste tot unde se așteaptă.
export function HeartSpinner({size=46, label, style}) {
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12,padding:"22px 0",...style}}>
      <div className="heartspin" style={{fontSize:size,color:"var(--purple)",lineHeight:1}}>♥</div>
      {label&&<div style={{fontSize:13,color:"var(--text-3)",textAlign:"center"}}>{label}</div>}
    </div>
  );
}

export function getInitials(name){
  if(!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if(parts.length===0) return "?";
  if(parts.length===1) return parts[0].slice(0,2).toUpperCase();
  return (parts[0][0]+parts[parts.length-1][0]).toUpperCase();
}
// Culoare stabilă derivată dintr-un id (același id → mereu aceeași culoare, pentru oricine se uită)
export function colorFromId(id){
  if(!id) return CAREGIVER_PALETTE[0];
  let h=0;
  const s=String(id);
  for(let i=0;i<s.length;i++){ h=(h*31 + s.charCodeAt(i))>>>0; }
  return CAREGIVER_PALETTE[h % CAREGIVER_PALETTE.length];
}
// Badge cu inițialele îngrijitorului care a introdus o intrare. La atingere/hover arată numele complet.
// Se afișează DOAR dacă intrarea are addedBy (introdusă de un îngrijitor, nu de owner).
// Dacă primește `family`, ia numele și culoarea din membrul de familie cu acel email (setate de owner).
export function CaregiverBadge({addedBy, addedByName, addedByEmail, family, size=18}) {
  const [show, setShow] = React.useState(false);
  if(!addedBy || !addedByName) return null;
  // Caută membrul de familie marcat ca îngrijitor cu acest email → ia nume + culoare setate de owner
  let name = addedByName;
  let color = colorFromId(addedBy);
  if(family && addedByEmail){
    const fam = family.find(f => f.caregiver && f.email && f.email.toLowerCase()===addedByEmail.toLowerCase());
    if(fam){
      if(fam.name) name = fam.name;
      if(fam.caregiverColor) color = fam.caregiverColor;
    }
  }
  const initials = getInitials(name);
  return (
    <span style={{display:"inline-flex",alignItems:"center",gap:5,flexShrink:0}}
      onMouseEnter={()=>setShow(true)} onMouseLeave={()=>setShow(false)}
      onClick={(e)=>{e.stopPropagation();setShow(s=>!s);}}>
      {show && (
        <span style={{
          background:color+"22",color:color,fontSize:11,fontWeight:600,
          padding:"2px 8px",borderRadius:10,whiteSpace:"nowrap",lineHeight:1.4}}>
          {name}
        </span>
      )}
      <span style={{
        width:size,height:size,borderRadius:"50%",background:color,
        display:"inline-flex",alignItems:"center",justifyContent:"center",
        fontSize:size*0.42,fontWeight:700,color:"var(--bg)",cursor:"pointer",
        lineHeight:1,letterSpacing:"-0.5px",flexShrink:0}}>
        {initials}
      </span>
    </span>
  );
}

// ─── SELECT CU CĂUTARE ───────────────────────────────────────────────────────
// Dropdown cu input de căutare, listă filtrată și sortată alfabetic.
// Dropdown custom, temizat (înlocuiește <select> nativ, al cărui popup rămâne întunecat
// pe telefoanele cu sistemul pe dark). options: array de string-uri SAU {v,l}.
export function Dropdown({options, value, onChange, placeholder="— Selectează —", style}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const opts = options.map(o => (o && typeof o==="object") ? o : {v:o, l:o});
  React.useEffect(()=>{
    function onDoc(e){ if(ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown",onDoc);
    return ()=>document.removeEventListener("mousedown",onDoc);
  },[]);
  const cur = opts.find(o=>String(o.v)===String(value));
  return (
    <div ref={ref} style={{position:"relative",...style}}>
      <button type="button" onClick={()=>setOpen(o=>!o)}
        style={{width:"100%",textAlign:"left",background:"var(--surface-2)",border:"1px solid var(--border-2)",borderRadius:8,
          color:cur?"var(--text)":"var(--text-3)",padding:"9px 12px",fontSize:14,fontFamily:"inherit",cursor:"pointer",
          display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
        <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{cur?t(cur.l):t(placeholder)}</span>
        <span style={{color:"var(--text-3)",fontSize:11,flexShrink:0}}>▼</span>
      </button>
      {open && (
        <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,zIndex:100,
          background:"var(--surface)",border:"1px solid var(--border-2)",borderRadius:10,boxShadow:"0 8px 28px rgba(0,0,0,.35)",
          maxHeight:280,overflowY:"auto"}}>
          {opts.map((o,idx)=> o.header ? (
            <div key={"h"+idx} style={{padding:"7px 12px 3px",fontSize:10,fontWeight:700,letterSpacing:".05em",color:"var(--blue)"}}>{t(o.l)}</div>
          ) : (
            <button key={String(o.v)} type="button" onClick={()=>{onChange(o.v);setOpen(false);}}
              style={{display:"block",width:"100%",textAlign:"left",background:String(o.v)===String(value)?"var(--blue-bg)":"none",
                border:"none",color:"var(--text)",padding:"10px 12px",fontSize:14,fontFamily:"inherit",cursor:"pointer"}}
              onMouseEnter={e=>{if(String(o.v)!==String(value))e.currentTarget.style.background="var(--blue-bg-soft)";}}
              onMouseLeave={e=>{if(String(o.v)!==String(value))e.currentTarget.style.background="none";}}>
              {t(o.l)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function SearchableSelect({options, value, onChange, placeholder="Caută...", sort=true, limit=100}) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const ref = React.useRef(null);
  const full = React.useMemo(()=>{
    let arr = [...options];
    if(sort) arr.sort((a,b)=>{
      const aEnd=/^alt(a|ul)$/i.test(a), bEnd=/^alt(a|ul)$/i.test(b);
      if(aEnd&&!bEnd) return 1;
      if(bEnd&&!aEnd) return -1;
      return a.localeCompare(b,"ro");
    });
    if(q.trim()){
      const nq = foldDia(q.trim());
      arr = arr.filter(o=>foldDia(o).includes(nq));
    }
    return arr;
  },[options,q,sort]);
  // Limităm câte rezultate randăm (lista de localități are ~14k intrări)
  const list = full.length>limit ? full.slice(0,limit) : full;
  const hiddenCount = full.length - list.length;
  React.useEffect(()=>{
    function onDoc(e){ if(ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown",onDoc);
    return ()=>document.removeEventListener("mousedown",onDoc);
  },[]);
  return (
    <div ref={ref} style={{position:"relative"}}>
      <button type="button" onClick={()=>{setOpen(o=>!o);setQ("");}}
        style={{width:"100%",textAlign:"left",background:"var(--surface-2)",border:"1px solid var(--border)",borderRadius:8,
          color:value?"var(--text)":"var(--text-3)",padding:"10px 12px",fontSize:14,fontFamily:"inherit",cursor:"pointer",
          display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
        <span style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{value?t(value):t(placeholder)}</span>
        <span style={{color:"var(--text-3)",fontSize:11,flexShrink:0}}>▼</span>
      </button>
      {open && (
        <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,zIndex:100,
          background:"var(--surface)",border:"1px solid var(--border-2)",borderRadius:10,boxShadow:"0 8px 28px rgba(0,0,0,.5)",
          maxHeight:280,display:"flex",flexDirection:"column",overflow:"hidden"}}>
          <div style={{padding:8,borderBottom:"1px solid var(--border)"}}>
            <input autoFocus value={q} onChange={e=>setQ(e.target.value)} placeholder={placeholder}
              style={{width:"100%",background:"var(--surface-2)",border:"1px solid var(--border)",borderRadius:7,
                color:"var(--text)",padding:"8px 10px",fontSize:14,fontFamily:"inherit",boxSizing:"border-box"}}/>
          </div>
          <div style={{overflowY:"auto",flex:1}}>
            {list.length===0 && <div style={{padding:"12px",color:"var(--text-3)",fontSize:13,textAlign:"center"}}>{t("Niciun rezultat")}</div>}
            {list.map(o=>(
              <button key={o} type="button" onClick={()=>{onChange(o);setOpen(false);setQ("");}}
                style={{display:"block",width:"100%",textAlign:"left",background:o===value?"var(--blue-bg)":"none",
                  border:"none",color:"var(--text)",padding:"10px 12px",fontSize:14,fontFamily:"inherit",cursor:"pointer"}}
                onMouseEnter={e=>{if(o!==value)e.currentTarget.style.background="var(--blue-bg-soft)";}}
                onMouseLeave={e=>{if(o!==value)e.currentTarget.style.background="none";}}>
                {t(o)}
              </button>
            ))}
            {hiddenCount>0 && <div style={{padding:"10px 12px",color:"var(--text-3)",fontSize:12,textAlign:"center"}}>încă {hiddenCount} rezultate — scrie ca să filtrezi</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// Selector de adresă: Localitate + Județ cu filtrare încrucișată.
// - alegi județul → localitățile se filtrează la acel județ
// - alegi localitatea → județele se filtrează la cele care conțin acea localitate
//   (dacă e una singură, se completează automat)
// Randează două celule (fragment) ca să se potrivească într-un grid 2 coloane.
export function AddressPicker({city, county, onChange, localityLabel="Localitate", countyLabel="Județ"}) {
  const [data, setData] = React.useState(null);
  React.useEffect(()=>{ let ok=true; loadLocalities().then(d=>{ if(ok) setData(d); }); return ()=>{ok=false;}; }, []);

  const allLocalities = React.useMemo(()=>{
    if(!data) return [];
    const s = new Set();
    Object.values(data).forEach(arr=>arr.forEach(n=>s.add(n)));
    return [...s].sort((a,b)=>a.localeCompare(b,"ro"));
  }, [data]);
  // Localitățile din județul ales apar primele, dar TOATE rămân căutabile
  // (poți oricând alege o localitate din alt județ — nu ești blocat).
  const localityOptions = React.useMemo(()=>{
    if(!data) return city ? [city] : [];
    if(county && Array.isArray(data[county])){
      const inCounty = new Set(data[county]);
      return [...data[county], ...allLocalities.filter(l=>!inCounty.has(l))];
    }
    return allLocalities;
  }, [data, county, allLocalities, city]);
  // Județele care conțin localitatea aleasă apar primele, dar toate cele 42 rămân disponibile
  const countyOptions = React.useMemo(()=>{
    if(!data || !city) return JUDETE;
    const match = JUDETE.filter(c => (data[c]||[]).includes(city));
    if(!match.length) return JUDETE;
    const set = new Set(match);
    return [...match, ...JUDETE.filter(c=>!set.has(c))];
  }, [data, city]);

  function pickLocality(v){
    let nextCounty = county;
    if(data){
      const matches = JUDETE.filter(c => (data[c]||[]).includes(v));
      if(matches.length===1) nextCounty = matches[0];          // o singură potrivire → completez județul
      else if(county && !matches.includes(county)) nextCounty = ""; // județul curent nu se potrivește → îl golesc
    }
    onChange({ city:v, county:nextCounty });
  }
  function pickCounty(v){
    let nextCity = city;
    if(data && city && Array.isArray(data[v]) && !data[v].includes(city)) nextCity = ""; // localitatea nu e în noul județ → o golesc
    onChange({ city:nextCity, county:v });
  }

  return (
    <>
      <div>
        <div className="lbl">{t(localityLabel)}</div>
        <SearchableSelect options={localityOptions} value={city||""} onChange={pickLocality} placeholder={t("Caută localitatea...")} sort={false}/>
      </div>
      <div>
        <div className="lbl">{t(countyLabel)}</div>
        <SearchableSelect options={countyOptions} value={county||""} onChange={pickCounty} placeholder={t("Caută județul...")} sort={false}/>
      </div>
    </>
  );
}

// Validare parolă: minim 6 caractere, o literă mare, o cifră, un semn special
export function PasswordRules() {
  return (
    <div style={{marginTop:8,padding:"10px 12px",background:"var(--bg)",border:"1px solid var(--border)",borderRadius:8}}>
      <div style={{fontSize:11,color:"var(--text-3)",marginBottom:6}}>{t("Din motive de securitate parola trebuie:")}</div>
      <ul style={{margin:0,paddingLeft:18,fontSize:11,color:"var(--text-2)",lineHeight:1.7}}>
        <li>{t("să fie compusă din cel puțin 6 caractere")}</li>
        <li>{t("să conțină cel puțin o majusculă")}</li>
        <li>{t("să conțină cel puțin o cifră")}</li>
        <li>{t("să conțină cel puțin un caracter special (ex: ! @ # ?)")}</li>
      </ul>
    </div>
  );
}
// Listă live a cerințelor de parolă — fiecare rând devine verde când e îndeplinit
export function PasswordChecklist({value}) {
  const rules = [
    { ok: (value||"").length>=6, txt: "minim 6 caractere" },
    { ok: /[A-ZĂÂÎȘȚ]/.test(value||""), txt: "o literă mare" },
    { ok: /[0-9]/.test(value||""), txt: "o cifră" },
    { ok: /[^A-Za-z0-9ĂÂÎȘȚăâîșț]/.test(value||""), txt: "un semn special (! @ # ?)" },
  ];
  return (
    <div style={{marginTop:6,display:"flex",flexDirection:"column",gap:3}}>
      {rules.map((r,i)=>(
        <div key={i} style={{fontSize:11.5,color:r.ok?"var(--green)":"var(--text-3)",display:"flex",alignItems:"center",gap:7}}>
          <span style={{width:13,textAlign:"center"}}>{r.ok?"✓":"○"}</span>{t(r.txt)}
        </div>
      ))}
    </div>
  );
}
export const PhoneInput = ({value, onChange, placeholder}) => {
  const valid = !value || isValidPhone(value);
  return (
    <div>
      <input value={value||""} onChange={e=>onChange(e.target.value)} placeholder={placeholder||"ex: 0722 123 456"} style={!valid?{borderColor:"#f87171!important"}:{}}/>
      {value && !valid && <div style={{fontSize:11,color:"var(--red)",marginTop:3}}>{t("⚠ Numărul trebuie să aibă 10 cifre")}</div>}
    </div>
  );
};

export function Modal({title,onClose,children,wide}) {
  useEffect(() => {
    window._formDirty = false;
    return () => { window._formDirty = false; };
  }, []);

  function handleClose() {
    if(window._formDirty) {
      fmConfirm(t("Ieși fără să salvezi? Modificările vor fi pierdute."),{okText:t("Ieși"),cancelText:t("Rămâi")}).then(ok=>{
        if(!ok) return;
        window._formDirty = false;
        onClose();
      });
      return;
    }
    onClose();
  }
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.82)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:16}}>
      <div style={{background:"var(--surface)",border:"1px solid var(--border-2)",borderRadius:16,padding:24,width:"100%",maxWidth:wide?700:440,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontWeight:700,fontSize:18}}>{typeof title==="string"?t(title):title}</div>
          <button onClick={handleClose} style={{background:"none",border:"none",color:"var(--text-3)",cursor:"pointer",fontSize:22,lineHeight:1,padding:"0 4px"}}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function TimeInput({value, onChange, defaultScrollTo}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("hour");
  const [inputMode, setInputMode] = useState("clock");
  const [keyboardVal, setKeyboardVal] = useState(value||"");
  // Valoare internă temporară cât timp ceasul e deschis (nu trimite la parent până la OK)
  const [tempValue, setTempValue] = useState(value||"00:00");
  const [displayHour, setDisplayHour] = useState(null);
  const [displayMinute, setDisplayMinute] = useState(null);

  // La deschidere, sincronizează valoarea internă cu cea din parent
  function openPicker(){
    setTempValue(value||"00:00");
    setKeyboardVal(value||"");
    setMode("hour");
    setInputMode("clock");
    setOpen(true);
  }

  const parsed = (tempValue||"00:00").split(":");
  const currentHour = parseInt(parsed[0],10)||0;
  const currentMinute = parseInt(parsed[1],10)||0;
  const showHour = displayHour !== null ? displayHour : currentHour;
  const showMinute = displayMinute !== null ? displayMinute : currentMinute;

  const size = 260;
  const r = size/2;

  function hourPos(h, inner=false) {
    const angle = (h % 12) * 30 - 90;
    const rad = angle * Math.PI / 180;
    const radius = inner ? r * 0.42 : r * 0.72;
    return { x: r + radius * Math.cos(rad), y: r + radius * Math.sin(rad) };
  }
  function minPos(m) {
    const angle = m * 6 - 90;
    const rad = angle * Math.PI / 180;
    return { x: r + r * 0.72 * Math.cos(rad), y: r + r * 0.72 * Math.sin(rad) };
  }

  // Calcul unghi ac pentru animație
  const handAngle = mode==="hour"
    ? (showHour % 12) * 30
    : showMinute * 6;
  const handRadius = mode==="hour"
    ? (showHour >= 12 ? r * 0.42 : r * 0.72)
    : r * 0.72;
  const handX = r + handRadius * Math.sin(handAngle * Math.PI / 180);
  const handY = r - handRadius * Math.cos(handAngle * Math.PI / 180);

  const selX = mode==="hour" ? hourPos(showHour, showHour>=12).x : minPos(showMinute).x;
  const selY = mode==="hour" ? hourPos(showHour, showHour>=12).y : minPos(showMinute).y;

  function handleClockClick(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = rect.width/2, cy = rect.height/2;
    const x = e.clientX - rect.left - cx;
    const y = e.clientY - rect.top - cy;
    const angle = Math.atan2(x, -y) * 180 / Math.PI;
    const normalized = (angle + 360) % 360;

    if(mode === "hour") {
      const hour = Math.round(normalized / 30) % 12;
      const dist = Math.sqrt(x*x + y*y);
      const finalHour = dist < r * 0.58 ? (hour === 0 ? 0 : hour + 12) : (hour === 0 ? 12 : hour);
      setDisplayHour(finalHour);
      setTempValue(`${String(finalHour).padStart(2,"0")}:${String(currentMinute).padStart(2,"0")}`);
      setTimeout(()=>{ setMode("minute"); setDisplayHour(null); }, 200);
    } else {
      const minute = Math.round(normalized / 6) % 60;
      setDisplayMinute(minute);
      setTempValue(`${String(currentHour).padStart(2,"0")}:${String(minute).padStart(2,"0")}`);
      setTimeout(()=>{ setDisplayMinute(null); }, 200);
    }
  }

  function handleClockTouch(e) {
    e.preventDefault();
    const touch = e.touches[0] || e.changedTouches[0];
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = rect.width/2, cy = rect.height/2;
    const x = touch.clientX - rect.left - cx;
    const y = touch.clientY - rect.top - cy;
    const angle = Math.atan2(x, -y) * 180 / Math.PI;
    const normalized = (angle + 360) % 360;

    if(mode === "hour") {
      const hour = Math.round(normalized / 30) % 12;
      const dist = Math.sqrt(x*x + y*y);
      const finalHour = dist < r * 0.58 ? (hour === 0 ? 0 : hour + 12) : (hour === 0 ? 12 : hour);
      setDisplayHour(finalHour);
      setTempValue(`${String(finalHour).padStart(2,"0")}:${String(currentMinute).padStart(2,"0")}`);
      setTimeout(()=>{ setMode("minute"); setDisplayHour(null); }, 200);
    } else {
      const minute = Math.round(normalized / 6) % 60;
      setDisplayMinute(minute);
      setTempValue(`${String(currentHour).padStart(2,"0")}:${String(minute).padStart(2,"0")}`);
      setTimeout(()=>{ setDisplayMinute(null); }, 200);
    }
  }

  return (
    <div style={{position:"relative"}}>
      <button type="button" onClick={openPicker}
        style={{width:"100%",background:"var(--bg)",color:"var(--text)",border:"1px solid var(--border-2)",borderRadius:8,padding:"8px 12px",fontFamily:"monospace",fontSize:14,textAlign:"center",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
        <span>{value||"--:--"}</span>
        <span style={{fontSize:10,color:"var(--text-3)"}}>▾</span>
      </button>

      {open&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:"16px"}}
          onClick={e=>{if(e.target===e.currentTarget)setOpen(false);}}>
          <div style={{background:"var(--surface)",border:"1px solid var(--border-2)",borderRadius:20,padding:20,width:"100%",maxWidth:340,maxHeight:"90vh",overflowY:"auto"}}>

            {/* Time display */}
            <div style={{textAlign:"center",marginBottom:16,fontFamily:"monospace",fontSize:28,fontWeight:700,letterSpacing:2}}>
              <span onClick={()=>{setMode("hour");setInputMode("clock");}}
                style={{cursor:"pointer",color:mode==="hour"&&inputMode==="clock"?"var(--blue)":"var(--text-3)",
                  padding:"4px 10px",borderRadius:8,background:mode==="hour"&&inputMode==="clock"?"var(--blue-bg)":"none",
                  transition:"all 0.2s"}}>
                {String(currentHour).padStart(2,"0")}
              </span>
              <span style={{color:"var(--text-4)"}}>:</span>
              <span onClick={()=>{setMode("minute");setInputMode("clock");}}
                style={{cursor:"pointer",color:mode==="minute"&&inputMode==="clock"?"var(--blue)":"var(--text-3)",
                  padding:"4px 10px",borderRadius:8,background:mode==="minute"&&inputMode==="clock"?"var(--blue-bg)":"none",
                  transition:"all 0.2s"}}>
                {String(currentMinute).padStart(2,"0")}
              </span>
            </div>

            {inputMode==="clock" ? (
              <div style={{display:"flex",justifyContent:"center"}}>
                <svg width={size} height={size}
                  onClick={handleClockClick}
                  onTouchEnd={handleClockTouch}
                  style={{cursor:"pointer",display:"block",touchAction:"none"}}>

                  {/* Fundal cerc */}
                  <circle cx={r} cy={r} r={r-2} fill="var(--surface-2)" stroke="var(--border)" strokeWidth={1.5}/>

                  {/* Ac cu tranziție lină */}
                  <line x1={r} y1={r} x2={handX} y2={handY}
                    stroke="#8b5cf6" strokeWidth={2.5} strokeLinecap="round"
                    style={{transition:"x2 0.2s ease, y2 0.2s ease"}}/>

                  {/* Punct central */}
                  <circle cx={r} cy={r} r={4} fill="#8b5cf6"/>

                  {/* Cerc selector selectat */}
                  <circle cx={selX} cy={selY} r={18} fill="#7c3aed" opacity={0.95}
                    style={{transition:"cx 0.2s ease, cy 0.2s ease"}}/>

                  {/* Ore exterioare (1-12) */}
                  {mode==="hour"&&[...Array(12)].map((_,i)=>{
                    const h = i === 0 ? 12 : i;
                    const pos = hourPos(h, false);
                    const active = currentHour%12 === h%12 && currentHour < 12;
                    return <text key={h} x={pos.x} y={pos.y} textAnchor="middle"
                      dominantBaseline="central" fontSize={14} fontWeight={active?700:400}
                      fill={active?"#fff":"var(--text-2)"}>{h}</text>;
                  })}

                  {/* Ore interioare (0, 13-23) */}
                  {mode==="hour"&&[...Array(12)].map((_,i)=>{
                    const h = i === 0 ? 0 : i+12;
                    const pos = hourPos(h, true);
                    const active = currentHour === h;
                    return <text key={`i${h}`} x={pos.x} y={pos.y} textAnchor="middle"
                      dominantBaseline="central" fontSize={11} fontWeight={active?700:400}
                      fill={active?"#fff":"var(--text-3)"}>{h===0?"00":h}</text>;
                  })}

                  {/* Minute */}
                  {mode==="minute"&&[0,5,10,15,20,25,30,35,40,45,50,55].map(m=>{
                    const pos = minPos(m);
                    const active = currentMinute === m;
                    return <text key={m} x={pos.x} y={pos.y} textAnchor="middle"
                      dominantBaseline="central" fontSize={13} fontWeight={active?700:400}
                      fill={active?"#fff":"var(--text-2)"}>{String(m).padStart(2,"0")}</text>;
                  })}
                </svg>
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"30px 0"}}>
                <input autoFocus value={keyboardVal}
                  onChange={e=>{let v=e.target.value.replace(/[^0-9:]/g,"");if(v.length>5)v=v.slice(0,5);setKeyboardVal(v);}}
                  onBlur={e=>{
                    const parts=e.target.value.split(":");
                    if(parts.length===2){
                      const h=Math.min(23,Math.max(0,+parts[0]||0));
                      const m=Math.min(59,Math.max(0,+parts[1]||0));
                      const formatted=`${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`;
                      setKeyboardVal(formatted);onChange(formatted);
                    }
                  }}
                  placeholder="HH:MM" maxLength={5}
                  style={{fontFamily:"monospace",fontSize:32,textAlign:"center",background:"none",border:"none",
                    borderBottom:"2px solid var(--blue)",color:"var(--text)",width:130,outline:"none"}}/>
              </div>
            )}

            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:14}}>
              <button onClick={()=>{
                if(inputMode==="clock"){
                  // Trec la tastatură: pornesc de la valoarea aleasă pe ceas (nu de la cea inițială)
                  setKeyboardVal(tempValue);
                  setInputMode("keyboard");
                } else {
                  // Trec la ceas: preiau ce s-a tastat, ca să nu se piardă
                  const parts=(keyboardVal||"").split(":");
                  if(parts.length===2){
                    const h=Math.min(23,Math.max(0,+parts[0]||0));
                    const mn=Math.min(59,Math.max(0,+parts[1]||0));
                    setTempValue(`${String(h).padStart(2,"0")}:${String(mn).padStart(2,"0")}`);
                  }
                  setInputMode("clock");
                }
              }}
                style={{background:"none",border:"none",color:"var(--text-3)",cursor:"pointer",fontSize:20,padding:"4px 8px"}}>
                {inputMode==="clock" ? "⌨" : "🕐"}
              </button>
              <button onClick={()=>{
                if(inputMode==="keyboard"){
                  const parts=keyboardVal.split(":");
                  if(parts.length===2){
                    const h=Math.min(23,Math.max(0,+parts[0]||0));
                    const min=Math.min(59,Math.max(0,+parts[1]||0));
                    onChange(`${String(h).padStart(2,"0")}:${String(min).padStart(2,"0")}`);
                  }
                } else {
                  onChange(tempValue);
                }
                setOpen(false);
              }} style={{background:"#7c3aed",border:"none",borderRadius:10,color:"#fff",
                cursor:"pointer",padding:"8px 24px",fontSize:14,fontFamily:"inherit",fontWeight:600}}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function DatePicker({value, onChange, placeholder="ZZ.LL.AAAA"}) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(null);
  const [viewMonth, setViewMonth] = useState(null);
  const [pickingYear, setPickingYear] = useState(false);

  // Parse value DD.MM.YYYY
  function parseVal(v) {
    if(!v) return {d:null,m:null,y:null};
    const parts = v.split(".");
    if(parts.length!==3) return {d:null,m:null,y:null};
    return {d:+parts[0],m:+parts[1]-1,y:+parts[2]};
  }
  const parsed = parseVal(value);

  function initView() {
    const y = parsed.y || new Date().getFullYear()-50;
    const m = parsed.m!=null ? parsed.m : 0;
    setViewYear(y); setViewMonth(m); setPickingYear(false);
  }

  function toggleOpen() { if(!open) initView(); setOpen(o=>!o); }

  function selectDay(d) {
    const dd = String(d).padStart(2,"0");
    const mm = String(viewMonth+1).padStart(2,"0");
    onChange(`${dd}.${mm}.${viewYear}`);
    setOpen(false);
  }

  function selectYear(y) { setViewYear(y); setPickingYear(false); }

  const dim = getDIM(viewYear||2000, viewMonth||0);
  const fdow = getFDOW(viewYear||2000, viewMonth||0);
  const years = Array.from({length:120},(_,i)=>new Date().getFullYear()-i);

  return (
    <div style={{position:"relative"}}>
      <div style={{display:"flex",gap:8,alignItems:"center"}}>
        <input value={value||""} onChange={e=>onChange(e.target.value)} placeholder={placeholder} style={{flex:1}}/>
        <button type="button" onClick={toggleOpen} style={{background:"var(--border)",border:"1px solid var(--border-2)",borderRadius:8,padding:"7px 10px",cursor:"pointer",color:"var(--text-2)",fontSize:14,flexShrink:0}}>📅</button>
      </div>
      {open && (
        <div onClick={(e)=>{if(e.target===e.currentTarget)setOpen(false);}}
          style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(0,0,0,.5)",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div onClick={e=>e.stopPropagation()} style={{background:"var(--surface)",border:"1px solid var(--border-2)",borderRadius:12,padding:14,width:280,maxWidth:"90vw",maxHeight:"85vh",overflowY:"auto",boxShadow:"0 8px 32px rgba(0,0,0,.6)"}}>
          {/* Header */}
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <button onClick={()=>setViewMonth(m=>{const nm=m-1; if(nm<0){setViewYear(y=>y-1);return 11;} return nm;})} style={{background:"none",border:"none",color:"var(--text-2)",cursor:"pointer",fontSize:16}}>‹</button>
            <div style={{display:"flex",gap:6}}>
              <div style={{width:140}}><Dropdown value={viewMonth} onChange={v=>setViewMonth(+v)} options={MONTHS.map((m,i)=>({v:i,l:m}))}/></div>
              <button onClick={()=>setPickingYear(p=>!p)} style={{background:"var(--border)",border:"1px solid var(--border-2)",borderRadius:6,color:"var(--blue)",cursor:"pointer",fontSize:13,padding:"2px 8px",fontWeight:700}}>{viewYear}</button>
            </div>
            <button onClick={()=>setViewMonth(m=>{const nm=m+1; if(nm>11){setViewYear(y=>y+1);return 0;} return nm;})} style={{background:"none",border:"none",color:"var(--text-2)",cursor:"pointer",fontSize:16}}>›</button>
          </div>
          {/* Year picker */}
          {pickingYear && (
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:4,maxHeight:180,overflowY:"auto",marginBottom:8}}>
              {years.map(y=>(
                <button key={y} onClick={()=>selectYear(y)} style={{background:y===viewYear?"#7c3aed":"var(--border)",border:"none",borderRadius:6,color:y===viewYear?"#fff":"var(--text-2)",cursor:"pointer",padding:"4px",fontSize:12,fontFamily:"monospace"}}>{y}</button>
              ))}
            </div>
          )}
          {/* Calendar grid */}
          {!pickingYear && (
            <div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:4}}>
                {WDAYS.map(d=><div key={d} style={{textAlign:"center",fontSize:10,color:"var(--text-4)",fontFamily:"monospace"}}>{d}</div>)}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
                {Array(fdow).fill(null).map((_,i)=><div key={"e"+i}/>)}
                {Array(dim).fill(null).map((_,i)=>{
                  const d=i+1;
                  const isSelected = parsed.d===d && parsed.m===viewMonth && parsed.y===viewYear;
                  return (
                    <button key={d} onClick={()=>selectDay(d)} style={{background:isSelected?"#7c3aed":"none",border:"none",borderRadius:6,color:isSelected?"#fff":"var(--text)",cursor:"pointer",padding:"5px 2px",fontSize:12,textAlign:"center"}}>
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        </div>
      )}
    </div>
  );
}

// Wrapper peste DatePicker care lucrează cu format ISO (YYYY-MM-DD), în română, Luni prima
export function DateField({value, onChange, placeholder, style}) {
  function isoToDisplay(iso){ if(!iso) return ""; const p=iso.split("-"); return p.length===3?`${p[2]}.${p[1]}.${p[0]}`:iso; }
  function displayToIso(d){ const p=(d||"").split("."); return p.length===3?`${p[2]}-${p[1].padStart(2,"0")}-${p[0].padStart(2,"0")}`:d; }
  return (
    <div style={style}>
      <DatePicker value={isoToDisplay(value)} placeholder={placeholder} onChange={v=>onChange(displayToIso(v))}/>
    </div>
  );
}

export function Toggle({label, sub, on, onChange}) {
  return (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
      <div style={{flex:1}}>
        <div style={{fontSize:13,color:"var(--text)"}}>{typeof label==="string"?t(label):label}</div>
        {sub&&<div style={{fontSize:11,color:"var(--text-3)",marginTop:2,lineHeight:1.4}}>{typeof sub==="string"?t(sub):sub}</div>}
      </div>
      <button onClick={()=>onChange(!on)} style={{flexShrink:0,width:44,height:25,borderRadius:13,border:"none",cursor:"pointer",
        background:on?"#2563eb":"var(--border-2)",position:"relative",transition:"background .2s",padding:0}}>
        <span style={{position:"absolute",top:2,left:on?22:2,width:21,height:21,borderRadius:"50%",background:"#fff",transition:"left .2s"}}/>
      </button>
    </div>
  );
}

